import { Inject, Injectable } from '@angular/core';
import { ApiService } from '../api/api.service';
import { BehaviorSubject, Observable, delay, map, of, tap } from 'rxjs';
import { InStoreProduct } from '../api/contracts/product';
import { HttpMethods } from '../api/api.enums';
import { environment } from 'src/environments/environment';
import { FindByBarcodeReq } from '../api/contracts/in-store';
import { AppEventsService } from './app-events.service';
import { StorageKeys, StorageService } from './storage.service';
import { DeliveryLocation } from '../api/contracts/location';
import { InStoreSession } from '../types/in-store';
import { APP_CONFIG, AppConfig } from '../config/app-config';

@Injectable({
  providedIn: 'root',
})
export class InStoreProductsService {
  public readonly newProducts$ = new BehaviorSubject<InStoreProduct[] | null>(
    null
  );

  public readonly selectedStoreLocation$ =
    new BehaviorSubject<DeliveryLocation | null>(null);

  public currentInstoreSession$ = new BehaviorSubject<InStoreSession | null>(
    null
  );

  constructor(
    private readonly apiService: ApiService,
    private readonly appEventService: AppEventsService,
    private readonly storageService: StorageService,
    @Inject(APP_CONFIG) private readonly appConfig: AppConfig
  ) {
    this.appEventService.afterLocationChanged.subscribe(() => {
      this.newProducts$.next(null);
      this.storageService.removeFromSessionStorage(
        StorageKeys.NEW_PRODUCTS_SHOWN
      );
    });
  }

  retrieveInStoreProducts(locationId: string): Observable<InStoreProduct[]> {
    return this.apiService
      .request<InStoreProduct[]>({
        method: HttpMethods.GET,
        url: `${environment.apiUrl}/store_products/${locationId}`,
      })
      .pipe(
        map((res) => res.body as InStoreProduct[]),
        tap((products) => this.newProducts$.next(products as InStoreProduct[]))
      );
  }

  retrieveInStoreProduct(
    productId: string,
    locationHash: string
  ): Observable<InStoreProduct> {
    return this.apiService
      .request<InStoreProduct>({
        method: HttpMethods.GET,
        url: `${environment.apiUrl}/store_products/${locationHash}/${productId}`,
      })
      .pipe(map((res) => res.body as InStoreProduct));
  }

  findInStoreProductByBarcode(
    barcode: string,
    locationHash: string
  ): Observable<InStoreProduct> {
    return this.apiService
      .request<InStoreProduct, null, FindByBarcodeReq>({
        method: HttpMethods.GET,
        url: `${environment.apiUrl}/store_products/${locationHash}/barcode/${barcode}`,
      })
      .pipe(map((res) => res.body as InStoreProduct));
  }

  findInStoreProductByName(
    text: string,
    locationHash: string
  ): Observable<InStoreProduct[]> {
    return this.apiService
      .request<InStoreProduct[], null>({
        method: HttpMethods.GET,
        url: `${environment.apiUrl}/store_products/${locationHash}/search/${text}`,
      })
      .pipe(map((res) => res.body as InStoreProduct[]));
  }

  checkNewProductsByLocation(locationId: string): Observable<InStoreProduct[]> {
    return this.retrieveInStoreProducts(locationId).pipe(
      tap((products) => this.newProducts$.next(products as InStoreProduct[]))
    );
  }

  startInstoreSession(location: DeliveryLocation): void {
    const session: InStoreSession = {
      startedAt: Date.now(),
      location,
    };
    this.currentInstoreSession$.next(session);
    this.storageService
      .set<DeliveryLocation>(StorageKeys.LAST_USED_INSTORE_LOCATION, location)
      .subscribe();
  }

  finishInstoreSession(): void {
    this.currentInstoreSession$.next(null);
  }

  hasCurrentInstoreSessionExpired(): boolean {
    const storedTimestamp = this.currentInstoreSession$.getValue()?.startedAt;
    if (!storedTimestamp) {
      return true;
    }
    const now = Date.now();
    const diff = now - storedTimestamp;
    const expired = diff > this.appConfig.instorePurchases.sessionLength;
    if (expired) {
      this.currentInstoreSession$.next(null);
    }
    return expired;
  }
}
